import Component from "flarum/common/Component";
import app from "flarum/forum/app";
import StreamPlayer from './StreamPlayer';
import StreamService from './StreamService';


export default class AudioPlayer extends Component {
  oninit(vnode) {
    const streamUrl = app.forum.attribute("radioStreamUrl");
    if (!streamUrl) return;

    this.stream = new StreamPlayer(streamUrl, 30);

    const wsUrl = app.forum.attribute("radioWsUrl");
    const wsStation = app.forum.attribute("radioStation");
    this.socket = wsUrl ? new StreamService(wsUrl, wsStation) : null;

    this.playing = false;
    this.progressTimer = null;
    this.volumeRaf = null;
    this.volumePercentage = 10;
    this.volumeMode = 'low';
    this.minimized = true;
    this.volumeButtonContent = null;
    this.volumeTrackEl = null;

    this.stream.setVolume(0.1);

    this.stream.on('play', this.onPlay);
    this.stream.on('pause', this.onPause);
    this.stream.on('volumechange', this.onVolumeChange);

    this.socket?.on('trackUpdate', this.onProgress);
    //this.socket?.pause();

    this.track = {
      id: '',
      title: '-',
      artist: '-',
      duration: 0,
      startedAt: 0,
      elapsed: 0,
      progressPercentage: 0,
      currentTime: 0,
      isLive: false
    };

    this.playerEl = null;
    this.draggingPlayer = false;
    this.dragOffsetX = 0;
    this.dragOffsetY = 0;

    let savedPos = {x: 0, y: 0, stickToRight: false};
    const vw = document.documentElement.clientWidth;
    const vh = document.documentElement.clientHeight;

    try {
      let parsed = JSON.parse(localStorage.getItem('audioPlayerPos'));
      if (Number(parsed.x)) savedPos.x = parsed.x;
      if (Number(parsed.y)) savedPos.y = parsed.y;
      if (!!parsed.stickToRight) savedPos.stickToRight = true;
    } catch (e) {}

    this.playerPos = {
      x: Math.min(savedPos?.x ?? 0, vw), // resize check
      y: Math.min(savedPos?.y ?? 0, vh - 50), // 50px, because it's player's height atm
    };

    this.stickToRight = savedPos.stickToRight;
    this.forceExpand = false;
    this.showVolumeSlider = false;

    this.dragExcludeSelectors = [
      '.play-button',
      '.volume-slider',
      '.volume-control',
      '.progress-thumb'
    ];

    this.playIcon = (
      <svg id="playIcon" width="30" height="30" viewBox="0 0 50 50" fill="white">
        <circle cx="25" cy="25" r="22" stroke="white" stroke-width="3" fill="transparent"></circle>
        <path d="M20 15v20l15-10z" fill="white"></path>
      </svg>
    );

    this.pauseIcon = (
      <svg width="30" height="30" viewBox="0 0 24 24" fill="white">
        <circle cx="12" cy="12" r="10" stroke="white" stroke-width="2" fill="transparent"></circle>
        <rect x="8.5" y="7" width="3" height="10" fill="white"></rect>
        <rect x="12.5" y="7" width="3" height="10" fill="white"></rect>
      </svg>
    );

    this.volumeIcons = {
      muted: [
        <line x1="22" y1="15" x2="16" y2="9" />,
        <line x1="22" y1="9" x2="16" y2="15" />
      ],
      low: <path d="M16 8.5a5.5 5.5 0 010 7" stroke-linecap="butt" />,
      high: [
        <path d="M16 8.5a5.5 5.5 0 010 7" stroke-linecap="butt" />,
        <path d="M18 6a8 8 0 010 12" stroke-linecap="butt" />
      ]
    };

    this.isMobile = window.matchMedia?.('(hover: none) and (pointer: coarse)').matches;
  }

  oncreate(vnode) {
    window.addEventListener('mouseup', this.volumeDragEnd);
    window.addEventListener('mousemove', this.volumeDragging);

    if (!this.isMobile) {
      window.addEventListener('mousemove', this.playerDragMove);
      window.addEventListener('mouseup', this.playerDragEnd);
    }
  }

  onremove(vnode) {
    window.removeEventListener('mouseup', this.volumeDragEnd);
    window.removeEventListener('mousemove', this.volumeDragging);

    if (!this.isMobile) {
      window.removeEventListener('mousemove', this.playerDragMove);
      window.removeEventListener('mouseup', this.playerDragEnd);
    }

    if (this.progressTimer) {
      cancelAnimationFrame(this.progressTimer);
    }

    this.socket?.destroy();
    this.stream?.destroy?.();
  }

  onPause = () => {
    this.playing = false;
    m.redraw();
  }

  onPlay = () => {
    this.playing = true;

    // note [test]: we do this, because we stop progress time on pause
    // and want to sync track on play without waiting for the next 'trackUpdate' event
    if (!this.progressTimer && this.track.startedAt) {
      this.onProgress(this.track);
    }

    m.redraw();
  }

  onVolumeChange = () => {
    if (this.stream.audio.volume == 0) this.volumeMode = 'muted';
    else if (this.stream.audio.volume < 0.5) this.volumeMode = 'low';
    else this.volumeMode = 'high';

    this.volumePercentage = Math.min(this.stream.audio.volume * 100, 91); // so the ui will look ok

    if (!this.volumeRaf) {
      this.volumeRaf = requestAnimationFrame(() => {
        m.redraw();
        this.volumeRaf = null;
      });
    }
  };

  togglePlay = (e) => {
    if (this.isMobile && this.touchstartX != null) {
      const touchendX = e.changedTouches?.[0]?.screenX ?? e.screenX;
      const touchendY = e.changedTouches?.[0]?.screenY ?? e.screenY;

      const dx = Math.abs(touchendX - this.touchstartX);
      const dy = Math.abs(touchendY - this.touchstartY);

      // Consider it a swipe if moved more than 30px in any direction
      if (dx > 30 || dy > 30) {
        this.touchstartX = null;
        this.touchstartY = null;
        return; // ignore, it’s a swipe
      }
    }

    this.touchstartX = null;
    this.touchstartY = null;

    if (this.stream.audio.paused) {
      this.stream.play().then(() => {});
    } else {
      this.stream.pause();
    }
  }

  updateVolume = (clientX) => {
    if (!this.volumeTrackEl) return;

    const rect = this.volumeTrackEl.getBoundingClientRect();
    let x = clientX - rect.left;
    x = Math.max(0, Math.min(x, rect.width));

    if (x == 0 && rect.width == 0) {
        this.draggingVolume = false;
        return;
    };

    let percent = (x / rect.width) * 100;
    this.stream.setVolume(percent / 100);
  }

  volumeDragStart = (e) => {
    this.draggingVolume = true;
    this.updateVolume(e.clientX);
  }

  volumeDragging = (e) => {
    if (this.draggingVolume) {
      this.updateVolume(e.clientX);
    }
  }

  volumeDragEnd = () => {
    this.draggingVolume = false;
  }

  onProgress = (data) => {
    this.track.id = data.id;
    this.track.title = data.title;
    this.track.artist = data.artist;
    this.track.duration = data.duration;
    this.track.elapsed = data.elapsed;
    this.track.startedAt = data.startedAt;
    this.track.currentTime = data.currentTime;
    this.track.isLive = data.isLive;

    if (this.progressTimer) cancelAnimationFrame(this.progressTimer);

    let lastUpdate = 0;
    const TICK_INTERVAL = 1000 / 24;

    const updateProgress = () => {
      if (!this.playing) {
        this.progressTimer = null;
        return;
      }

      const now = performance.now();
      const elapsed = Math.min(Math.floor(Date.now() / 1000) - this.track.startedAt, this.track.duration);

      if (now - lastUpdate >= TICK_INTERVAL) {
        if (elapsed !== this.track.elapsed) {
          this.track.elapsed = elapsed;
          this.track.progressPercentage = Math.min(Math.max((elapsed / this.track.duration) * 100, 0), 91.5); // so the thumb will look nice
          if (this.playing) m.redraw();
        }

        lastUpdate = now;
      }

      if (elapsed < this.track.duration) {
        this.progressTimer = requestAnimationFrame(updateProgress);
      } else {
        this.progressTimer = null;
      }
    };

    updateProgress();
  }

  formatTime(sec) {
    const hrs = Math.max(Math.floor(sec / 3600), 0);
    sec %= 3600;
    const mns = Math.max(Math.floor(sec / 60), 0);
    const s = Math.max(Math.floor(sec % 60), 0);

    return (hrs ? hrs.toString().padStart(2, '0') + ':' : '') +
      (mns ? mns.toString().padStart(2, '0') : '00') + ':' +
      s.toString().padStart(2, '0');
  }

  toggleMinimize = () => {
    this.minimized = !this.minimized;
  }

  onPlayerCreated = (vnode) => {
    this.playerEl = vnode.dom;

    if (this.isMobile) {
      this.playerEl.addEventListener('touchstart', this.playerTouchStart, { passive: false });
      this.playerEl.addEventListener('touchend', this.playerTouchEnd);
    } else {
      this.playerEl.addEventListener('mousedown', this.playerDragStart);

      const el = this.playerEl;
      const vw = document.documentElement.clientWidth;

      if (this.stickToRight) {
        this.playerPos.xFromRight = vw - 275 - this.playerPos.x; // 275 because expanded player's width is 275px now
        el.style.left = 'auto';
        el.style.right = '0';
        el.style.transform = `translateX(-${this.playerPos.xFromRight}px) translateY(-${this.playerPos.y}px)`;
      } else {
        el.style.left = '0';
        el.style.right = 'auto';
        el.style.transform = `translateX(${this.playerPos.x}px) translateY(-${this.playerPos.y}px)`;
      }
    }
  }

  playerDragStart = (e) => {
    const target = e.target;

    if (this.isMobile || !this.playerEl || this.dragExcludeSelectors.some(sel => target.closest(sel))) return;

    // before checking the selector
    this.draggingPlayer = true;

    const rect = e.currentTarget.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;

    this.dragOffsetX = clientX - rect.left;
    this.dragOffsetY = clientY - rect.top;

    e.preventDefault();
  }

  playerDragMove = (e) => {
    if (this.isMobile || !this.draggingPlayer || !this.playerEl) return;
    if (e.cancelable) e.preventDefault();

    this.lastClientX = e.touches ? e.touches[0].clientX : e.clientX;
    this.lastClientY = e.touches ? e.touches[0].clientY : e.clientY;

    if (!this.dragFrame) {
      this.dragFrame = requestAnimationFrame(this.playerUpdateDrag);
    }
  };

  playerUpdateDrag = () => {
    if (this.isMobile || !this.draggingPlayer || !this.playerEl) return;

    const el = this.playerEl;
    const vw = document.documentElement.clientWidth;
    const vh = document.documentElement.clientHeight;
    const width = el.offsetWidth;
    const height = el.offsetHeight;

    const oldPos = {...this.playerPos};
    const oldStickToRight = this.stickToRight;

    // X: constrain normally
    this.playerPos.x = Math.min(Math.max(this.lastClientX - this.dragOffsetX, 0), vw - width);

    // Y: invert for bottom-left default
    const top = this.lastClientY - this.dragOffsetY;
    const maxTop = vh - height;
    const clampedTop = Math.min(Math.max(top, 0), maxTop);
    this.playerPos.y = vh - height - clampedTop;

    // Determine if near right edge for sticky-right expand
    const stickToRight = this.playerPos.x > vw / 2;

    if (stickToRight) {
      // X translation from right
      this.playerPos.xFromRight = vw - width - this.playerPos.x;
      el.style.left = 'auto';
      el.style.right = '0';
      el.style.transform = `translateX(-${this.playerPos.xFromRight}px) translateY(-${this.playerPos.y}px)`;
      this.stickToRight = true;
    } else {
      el.style.left = '0';
      el.style.right = 'auto';
      el.style.transform = `translate(${this.playerPos.x}px, -${this.playerPos.y}px)`;
      this.stickToRight = false;
    }

    if (
      (oldPos.x !== 0 && this.playerPos.x == 0) ||
      (oldPos.x == 0 && this.playerPos.x !== 0) ||
      (oldStickToRight !== this.stickToRight) ||
      (oldPos.xFromRight == 0 && this.playerPos.xFromRight !== 0) ||
      (oldPos.xFromRight !== 0 && this.playerPos.xFromRight == 0)
    ) {
      m.redraw();
    }

    this.dragFrame = null;
  };

  playerDragEnd = () => {
    if (!this.isMobile && this.draggingPlayer) {
      this.draggingPlayer = false;
      if (this.dragFrame) cancelAnimationFrame(this.dragFrame);
      this.dragFrame = null;

      const vw = document.documentElement.clientWidth;
      this.stickToRight = this.playerPos.x > vw / 2;

      const savePos = {
        x: this.playerPos.x,
        y: this.playerPos.y,
        stickToRight: this.stickToRight
      };

      localStorage.setItem('audioPlayerPos', JSON.stringify(savePos));
    }
  };

  playerTouchStart = (e) => {
    if (!this.isMobile) return;
    this.touchstartX = e.changedTouches[0].screenX;
    this.touchstartY = e.changedTouches[0].screenY;
    this.touchingPlayer = true;
    e.preventDefault();
  }

  playerTouchEnd = (e) => {
    if (!this.isMobile) return;
    e.preventDefault();
    this.touchingPlayer = false;
    const touchendX = e.changedTouches[0].screenX
    let swipeDirection = null;
    let previousExpand = this.forceExpand;

    if (Math.abs(touchendX - this.touchstartX) < 50) return;

    if (touchendX < this.touchstartX) {
      swipeDirection = 'left';
      this.forceExpand = this.stickToRight ? true : false;
    }

    if (touchendX > this.touchstartX) {
      swipeDirection = 'right';
      this.forceExpand = this.stickToRight ? false : true;
    }

    if (this.forceExpand !== previousExpand) {
      m.redraw();
    }
  }

  // --- Mobile volume handlers ---
  onVolumeSliderCreated = (vnode) => {
    this.volumeTrackEl = vnode.dom;

    if (this.isMobile) {
      this.volumeTrackEl.addEventListener('touchmove', this.volumeTouchMove, { passive: false });
      this.volumeTrackEl.addEventListener('touchstart', this.volumeTouchStart);
      this.volumeTrackEl.addEventListener('touchend', this.volumeTouchEnd);
    } else {
      this.volumeTrackEl.addEventListener('mousedown', this.volumeDragStart);
      this.volumeTrackEl.addEventListener('mouseleave', this.volumeDragEnd);
    }
  };

  volumeTouchStart = (e) => {
    if (!this.isMobile) return;

    this.volumeTouchstartX = e.changedTouches[0].screenX;
    this.volumeTouchstartY = e.changedTouches[0].screenY;

    this.draggingVolume = true;
  };

  volumeTouchMove = (e) => {
    if (!this.isMobile || !this.draggingVolume || !this.showVolumeSlider) return;
    const touch = e.changedTouches[0];
    e.preventDefault(); // prevent scroll/back

    if (Math.abs(touch.screenX - this.volumeTouchstartX) < 10) return;

    this.updateVolume(touch.clientX);
  };

  volumeTouchEnd = (e) => {
    if (!this.isMobile) return;

    this.draggingVolume = false;

    const touch = e.changedTouches[0];

    if (Math.abs(touch.screenX - this.volumeTouchstartX) < 10) {
      this.showVolumeSlider = !this.showVolumeSlider;

      if (this.showVolumeSlider) {
        this.volumeTrackEl?.classList.add('shown');
      } else {
        this.volumeTrackEl?.classList.remove('shown');
      }
    }
  };

  volumeClickHandler = (e) => {
    if (!this.isMobile) {
      // desktop: toggle mute
      this.stream.setVolume(this.stream.volume == 0 ? 0.1 : 0);
      return;
    }

    // mobile: show slider and start dragging
    this.showVolumeSlider = !this.showVolumeSlider;

    if (this.showVolumeSlider) {
      this.volumeTrackEl?.classList.add('shown');
    } else {
      this.volumeTrackEl?.classList.remove('shown');
    }
  };

  view(vnode) {
    if (!this.stream) return;

    return (
      <div class="radio-wrapper" style={{ 
          position: 'fixed',
          cursor: this.draggingPlayer ? 'grabbing' : 'grab',
          zIndex: 1000 
        }}
        oncreate={this.onPlayerCreated}
      >
        <div class="radio-player" id="audioPlayer" style={{
            borderTopLeftRadius: this.playerPos.x == 0 ? '0' : '10px',
            borderBottomLeftRadius: this.playerPos.x == 0 ? '0' : '10px',
            borderTopRightRadius: this.playerPos.xFromRight == 0 || this.forceExpand ? '0' : '10px',
            borderBottomRightRadius: this.playerPos.xFromRight == 0 || this.forceExpand ? '0' : '10px',
            flexDirection: this.stickToRight ? 'row-reverse' : 'row',
            maxWidth: this.forceExpand ? '100vw' : '',
            width: this.forceExpand ? '100vw' : '',
          }}
        >
          <div class="play-button" id="playPause" onpointerup={this.togglePlay}>
            { this.playing ? this.pauseIcon : this.playIcon }
          </div>

          <div class="meta-container">
            <div class="marquee">
              <div class="marquee-content track-info" id="trackTitle">
                {this.track.isLive ? '[LIVE]' : ''} {this.track.title}
              </div>
            </div>

            <div class="progress-container">
              <div class="progress-track">
                <div class="progress-fill" style={{ width: `${this.track.progressPercentage}%` }}></div>
                <div class="progress-thumb" style={{ left: `${this.track.progressPercentage}%` }}></div>
              </div>
              <div class="time-container">
                <div class="time-info" id="trackElapsed">
                  {this.formatTime(this.track.elapsed)}
                </div>
                <div class="time-info" id="trackDuration">
                  {this.formatTime(this.track.duration)}
                </div>
              </div>
            </div>
          </div>

          <div class="volume-container">
            <div class="volume-slider" id="volumeSlider"
              oncreate={this.onVolumeSliderCreated}
              onclick={(e) => this.updateVolume(e.clientX)}
             >
              <div class="volume-track">
                <div class="volume-fill" style={{ width: `${this.volumePercentage}%` }}></div>
                <div class="volume-thumb" style={{ left: `${this.volumePercentage}%` }}></div>
              </div>
            </div>

            <div class="volume-control" onpointerdown={ this.volumeClickHandler }>
              <svg viewBox="0 0 24 24" fill="none"  stroke="white" stroke-width="1.5">
                <path d="M4 9v6h4l5 5V4L8 9H4z" stroke-linejoin="round"></path>
                <line x1="8" y1="9" x2="8" y2="15"></line>
                <g id="vw">{ this.volumeIcons[this.volumeMode]}</g>
              </svg>
            </div>
          </div>
        </div>
      </div>
    );
  }
}
